// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ArchivedIcon } from '@/components/archived-icon';
import { Button, IconButton } from '@mui/material';

type ArchiveControlVariant = 'icon' | 'button';

const DEFAULT_VARIANT: ArchiveControlVariant = 'icon';

interface Props {
  /**
   * The current archive status of the entity.
   */
  isArchived: boolean;

  /**
   * The variant of the control.
   *
   * @default 'icon'
   */
  variant?: ArchiveControlVariant;

  /**
   * If true, the control is disabled.
   */
  isDisabled?: boolean;

  /**
   * A callback fired when the archive is changed.
   */
  onChange: (isArchived: boolean) => void;
}

/**
 * A control that allows the user to archive or unarchive an entity.
 */

export function ArchiveControl(props: Props) {
  const variant = props.variant ?? DEFAULT_VARIANT;
  const label = props.isArchived ? 'Unarchive' : 'Archive';

  const handleClick = () => {
    if (props.isArchived) {
      return props.onChange(false);
    }

    return props.onChange(true);
  };

  const icon = <ArchivedIcon isArchived={props.isArchived} />;

  if (variant === 'icon') {
    return (
      <IconButton
        size="small"
        disabled={props.isDisabled}
        aria-label={label}
        onClick={handleClick}
      >
        {icon}
      </IconButton>
    );
  }

  return (
    <Button
      color="secondary"
      variant="contained"
      onClick={handleClick}
      disabled={props.isDisabled}
      startIcon={icon}
    >
      {label}
    </Button>
  );
}
